import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Fab, Badge, Box, Typography, TextField, IconButton, Avatar, Chip,
  CircularProgress, Paper, List, ListItem, ListItemAvatar, ListItemText,
  Slide, Zoom, Fade, alpha, Tooltip, Divider
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MinimizeIcon from '@mui/icons-material/Minimize';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { useSocket } from '../../context/SocketContext';
import { 
  postSaveWebMessagesApi, 
  getWebMessagesByQuestionIdApi,
  getWebMessagesByUserIdApi,
  getWebMessagesByReceiverUserIdApi,
  getWebMessagesBySenderUserIdApi
} from '../../api/webMessages';
import { getUserByIdApi } from '../../api/user';
import config from '../../config';
import { NotificationManager } from 'react-notifications';

const LiveChatWidget = ({ authUser, selectedProject }) => {
  const { socket, onlineUsers, isConnected, findSupport, sendTyping } = useSocket();
  
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeChat, setActiveChat] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [conversations, setConversations] = useState([]); // Mesajlaştığı kişiler
  const [typingUser, setTypingUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const [questionId, setQuestionId] = useState(0);

  // Mesaj scrollu
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleChatMessage = (message) => {
      console.log('Received chat message:', message);
      
      // Ses bildirimi çal
      playNotificationSound();
      
      // Sadece aktif sohbetten gelen mesajları ekle
      if (activeChat && message.senderUserId === activeChat.id) {
        setMessages(prev => [...prev, { ...message, type: 'received' }]);
        
        if (!open || minimized) {
          NotificationManager.info(
            message.body?.substring(0, 50) + '...',
            `${activeChat.name || 'Yeni mesaj'}`,
            3000
          );
        }
      } else if (!activeChat) {
        // Aktif sohbet yoksa bildirim göster
        NotificationManager.info('Yeni bir mesajınız var', 'Canlı Destek', 3000);
      }
      
      // Conversations'ı güncelle
      setConversations(prev => {
        const existingConv = prev.find(c => c.id === message.senderUserId);
        if (existingConv) {
          return prev.map(conv => 
            conv.id === message.senderUserId
              ? {
                  ...conv,
                  lastMessage: message.body,
                  lastMessageDate: message.createDate || new Date().toISOString(),
                  unreadCount: (conv.unreadCount || 0) + 1,
                  // Kullanıcı bilgilerini güncelle (varsa)
                  name: message.senderUser || message.senderUserName || conv.name,
                  profileImageUrl: message.senderUserImage || conv.profileImageUrl
                }
              : conv
          ).sort((a, b) => {
            if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
            if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
            const dateA = new Date(a.lastMessageDate || 0);
            const dateB = new Date(b.lastMessageDate || 0);
            return dateB - dateA;
          });
        } else {
          // Yeni conversation ekle - önce API'den kullanıcı bilgisini çek
          const fetchUserInfo = async () => {
            try {
              const userResult = await getUserByIdApi(message.senderUserId);
              if (userResult.status && userResult.user) {
                const userName = `${userResult.user.name || ''} ${userResult.user.surname || ''}`.trim();
                setConversations(prev => {
                  const newConv = {
                    id: message.senderUserId,
                    name: userName || `Kullanıcı ${message.senderUserId}`,
                    profileImageUrl: userResult.user.profileImageUrl || '',
                    status: 'offline',
                    lastMessage: message.body,
                    lastMessageDate: message.createDate || new Date().toISOString(),
                    unreadCount: 1,
                    isOnline: false
                  };
                  return [newConv, ...prev].sort((a, b) => {
                    if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
                    if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
                    const dateA = new Date(a.lastMessageDate || 0);
                    const dateB = new Date(b.lastMessageDate || 0);
                    return dateB - dateA;
                  });
                });
              }
            } catch (error) {
              console.log('User fetch error:', error);
            }
          };
          
          // Geçici olarak mesajdan gelen bilgiyi kullan
          const newConv = {
            id: message.senderUserId,
            name: message.senderUser || message.senderUserName || `Kullanıcı ${message.senderUserId}`,
            profileImageUrl: message.senderUserImage || '',
            status: 'offline',
            lastMessage: message.body,
            lastMessageDate: message.createDate || new Date().toISOString(),
            unreadCount: 1,
            isOnline: false
          };
          
          // Arka planda kullanıcı bilgisini çek
          fetchUserInfo();
          
          return [newConv, ...prev].sort((a, b) => {
            if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
            if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
            const dateA = new Date(a.lastMessageDate || 0);
            const dateB = new Date(b.lastMessageDate || 0);
            return dateB - dateA;
          });
        }
      });
    };

    const handleTyping = ({ senderId, senderName, isTyping }) => {
      if (activeChat && senderId === activeChat.id) {
        if (isTyping) {
          setTypingUser(senderName);
          // 3 saniye sonra typing'i kaldır
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setTypingUser(null);
          }, 3000);
        } else {
          setTypingUser(null);
        }
      }
    };

    const handleMessageSent = (message) => {
      console.log('Message sent confirmation:', message);
    };

    socket.on('chat:message', handleChatMessage);
    socket.on('chat:typing', handleTyping);
    socket.on('chat:message:sent', handleMessageSent);

    return () => {
      socket.off('chat:message', handleChatMessage);
      socket.off('chat:typing', handleTyping);
      socket.off('chat:message:sent', handleMessageSent);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [socket, activeChat, open, minimized]);

  // Online kullanıcılar değiştiğinde conversations'ı güncelle
  useEffect(() => {
    if (conversations.length > 0) {
      setConversations(prev => prev.map(conv => {
        const onlineUser = onlineUsers.find(u => u.id === conv.id);
        return {
          ...conv,
          status: onlineUser ? 'online' : 'offline',
          isOnline: !!onlineUser,
          name: onlineUser?.userName || conv.name,
          profileImageUrl: onlineUser?.profileImageUrl || conv.profileImageUrl
        };
      }));
    }
  }, [onlineUsers]);

  // Mesajlaştığı kişileri ve gelen mesajları yükle
  const loadConversations = async () => {
    if (!authUser) return;
    
    try {
      // Gelen mesajları çek
      const receivedResult = await getWebMessagesByReceiverUserIdApi(authUser.id);
      // Gönderilen mesajları çek
      const sentResult = await getWebMessagesBySenderUserIdApi(authUser.id);
      
      const allMessages = [
        ...(receivedResult.status ? receivedResult.data || [] : []),
        ...(sentResult.status ? sentResult.data || [] : [])
      ];
      
      // Kullanıcı ID'lerine göre grupla
      const conversationMap = new Map();
      
      allMessages.forEach(msg => {
        const otherUserId = msg.senderUserId === authUser.id 
          ? msg.receiverUserId 
          : msg.senderUserId;
        
        if (!conversationMap.has(otherUserId)) {
          // Mesajdan gelen kullanıcı bilgilerini al
          // Backend'den senderUser ve receiverUser geliyor
          const otherUserInfo = msg.senderUserId === authUser.id 
            ? {
                userName: msg.receiverUser || '',
                userImage: msg.receiverUserImage || ''
              }
            : {
                userName: msg.senderUser || '',
                userImage: msg.senderUserImage || ''
              };
          
          conversationMap.set(otherUserId, {
            userId: otherUserId,
            lastMessage: msg,
            unreadCount: 0,
            messages: [],
            userName: otherUserInfo.userName,
            userImage: otherUserInfo.userImage
          });
        }
        
        const conv = conversationMap.get(otherUserId);
        conv.messages.push(msg);
        
        // Son mesajı güncelle
        const msgDate = new Date(msg.createDate || msg.create_date || 0);
        const lastDate = new Date(conv.lastMessage.createDate || conv.lastMessage.create_date || 0);
        if (msgDate > lastDate) {
          conv.lastMessage = msg;
          // Kullanıcı bilgilerini de güncelle (eğer yoksa veya boşsa)
          if (!conv.userName || conv.userName.trim() === '') {
            if (msg.senderUserId === authUser.id) {
              conv.userName = msg.receiverUser || '';
              conv.userImage = msg.receiverUserImage || conv.userImage;
            } else {
              conv.userName = msg.senderUser || '';
              conv.userImage = msg.senderUserImage || conv.userImage;
            }
          }
        }
        
        // Okunmamış mesaj sayısı
        if (msg.receiverUserId === authUser.id && !msg.isRead) {
          conv.unreadCount++;
        }
      });
      
      // Kullanıcı bilgilerini al (mesajlardan, onlineUsers'dan veya API'den)
      const conversationsList = await Promise.all(
        Array.from(conversationMap.values()).map(async (conv) => {
          // Önce mesajdan gelen bilgiyi kullan
          let userName = conv.userName || '';
          let profileImageUrl = conv.userImage || '';
          
          // Online kullanıcılardan bilgi al (varsa güncelle)
          const onlineUser = onlineUsers.find(u => u.id === conv.userId);
          if (onlineUser) {
            userName = onlineUser.userName || userName;
            profileImageUrl = onlineUser.profileImageUrl || profileImageUrl;
          }
          
          // Eğer hala isim yoksa API'den çek
          if (!userName || userName.trim() === '') {
            try {
              const userResult = await getUserByIdApi(conv.userId);
              if (userResult.status && userResult.user) {
                userName = `${userResult.user.name || ''} ${userResult.user.surname || ''}`.trim();
                profileImageUrl = userResult.user.profileImageUrl || profileImageUrl;
              }
            } catch (error) {
              console.log('User fetch error for', conv.userId, error);
            }
          }
          
          return {
            id: conv.userId,
            name: userName || `Kullanıcı ${conv.userId}`,
            profileImageUrl: profileImageUrl || '',
            status: onlineUser ? 'online' : 'offline',
            lastMessage: conv.lastMessage.body,
            lastMessageDate: conv.lastMessage.createDate || conv.lastMessage.create_date,
            unreadCount: conv.unreadCount,
            isOnline: !!onlineUser
          };
        })
      )
        .sort((a, b) => {
          // Önce okunmamış mesajları, sonra tarihe göre sırala
          if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
          if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
          const dateA = new Date(a.lastMessageDate || 0);
          const dateB = new Date(b.lastMessageDate || 0);
          return dateB - dateA; // En yeni önce
        });
      
      setConversations(conversationsList);
      console.log('Loaded conversations:', conversationsList.length);
    } catch (error) {
      console.error('Load conversations error:', error);
      setConversations([]);
    }
  };

  // Destek personeli ara ve mesajlaştığı kişileri yükle
  const loadSupporters = async () => {
    setLoading(true);
    try {
      // Aktif personelleri ara
      const result = await findSupport(selectedProject?.id);
      console.log('Support found:', result);
      setSupporters(result.supporters || []);
      
      // Mesajlaştığı kişileri yükle
      await loadConversations();
    } catch (error) {
      console.error('Find support error:', error);
      setSupporters([]);
      await loadConversations();
    }
    setLoading(false);
  };

  // Chat'i aç
  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    if (!activeChat) {
      loadSupporters();
    }
  };

  // Unread count'u conversations'dan hesapla
  useEffect(() => {
    const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
    setUnreadCount(totalUnread);
  }, [conversations]);

  // Ses bildirimi çal
  const playNotificationSound = useCallback(() => {
    try {
      // Web Audio API ile basit bir bip sesi oluştur
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frekans (Hz)
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Ses çalınamadı:', error);
    }
  }, []);

  // Mesajları yükle
  const loadMessages = async (receiverId) => {
    if (!receiverId || !authUser) return;
    
    setLoading(true);
    try {
      // Kullanıcının tüm mesajlarını çek
      const result = await getWebMessagesByUserIdApi(authUser.id);
      
      if (result.status && result.data) {
        // İki kullanıcı arasındaki mesajları filtrele
        const conversationMessages = result.data.filter(msg => 
          (msg.senderUserId === authUser.id && msg.receiverUserId === receiverId) ||
          (msg.senderUserId === receiverId && msg.receiverUserId === authUser.id)
        );
        
        // Tarihe göre sırala
        conversationMessages.sort((a, b) => {
          const dateA = new Date(a.createDate || a.create_date || 0);
          const dateB = new Date(b.createDate || b.create_date || 0);
          return dateA - dateB;
        });
        
        // Formatla
        const formattedMessages = conversationMessages.map(msg => ({
          id: msg.id,
          body: msg.body,
          senderUserId: msg.senderUserId,
          receiverUserId: msg.receiverUserId,
          type: msg.senderUserId === authUser.id ? 'sent' : 'received',
          createDate: msg.createDate || msg.create_date,
          questionId: msg.questionId || msg.question_id || 0
        }));
        
        // İlk mesajın questionId'sini al (yoksa 0)
        if (formattedMessages.length > 0 && formattedMessages[0].questionId) {
          setQuestionId(formattedMessages[0].questionId);
        }
        
        setMessages(formattedMessages);
        console.log('Loaded messages:', formattedMessages.length);
      }
    } catch (error) {
      console.error('Load messages error:', error);
    }
    setLoading(false);
  };

  // Sohbet başlat (hem supporter hem de conversation'dan)
  const startChat = async (person) => {
    // Person objesi supporter veya conversation olabilir
    const chatPerson = {
      id: person.id,
      name: person.name,
      profileImageUrl: person.profileImageUrl || '',
      status: person.status || 'offline'
    };
    
    setActiveChat(chatPerson);
    setMessages([]);
    await loadMessages(chatPerson.id);
    
    // Okunmamış mesaj sayısını sıfırla
    setConversations(prev => prev.map(conv => 
      conv.id === chatPerson.id ? { ...conv, unreadCount: 0 } : conv
    ));
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Sohbeti bitir
  const endChat = () => {
    setActiveChat(null);
    setMessages([]);
    setTypingUser(null);
    setQuestionId(0);
    loadSupporters();
  };

  // Mesaj gönder
  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    
    const messageObj = {
      body: newMessage.trim(),
      senderUserId: authUser.id,
      receiverUserId: activeChat?.id || 0,
      itemId: -1,
      questionId: questionId || 0, // Mevcut questionId'yi kullan
      isComplate: 0,
      isRead: 0,
      inst: selectedProject?.inst || authUser.inst,
      journalId: selectedProject?.id || 0,
      eMail: false,
      sms: false
    };

    try {
      // Mesajı API'ye kaydet
      const result = await postSaveWebMessagesApi(messageObj);
      
      if (result.status) {
        const savedMessage = {
          ...messageObj,
          id: result.id,
          type: 'sent',
          createDate: new Date().toISOString()
        };
        
        // İlk mesajsa questionId'yi kaydet
        if (!questionId && result.data?.questionId) {
          setQuestionId(result.data.questionId);
        } else if (!questionId && result.id) {
          // Eğer questionId yoksa, mesaj ID'sini questionId olarak kullan
          setQuestionId(result.id);
        }
        
        setMessages(prev => [...prev, savedMessage]);
        
        // Conversations'ı güncelle
        setConversations(prev => {
          const existingConv = prev.find(c => c.id === activeChat.id);
          if (existingConv) {
            return prev.map(conv => 
              conv.id === activeChat.id
                ? {
                    ...conv,
                    lastMessage: savedMessage.body,
                    lastMessageDate: savedMessage.createDate,
                    unreadCount: 0 // Gönderilen mesaj okunmuş sayılır
                  }
                : conv
            ).sort((a, b) => {
              if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
              if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
              const dateA = new Date(a.lastMessageDate || 0);
              const dateB = new Date(b.lastMessageDate || 0);
              return dateB - dateA;
            });
          }
          return prev;
        });
        
        // Socket ile gönder (realtime)
        if (socket && activeChat) {
          socket.emit('chat:message', {
            message: savedMessage,
            receiverId: activeChat.id
          });
        }
        
        setNewMessage('');
        sendTyping(activeChat?.id, false);
      } else {
        NotificationManager.error('Mesaj gönderilemedi', 'Hata');
      }
    } catch (error) {
      console.error('Send message error:', error);
      NotificationManager.error('Mesaj gönderilemedi', 'Hata');
    }
    
    setSending(false);
  };

  // Yazıyor bildirimi
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    if (activeChat) {
      sendTyping(activeChat.id, e.target.value.length > 0);
    }
  };

  // Enter ile gönder
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Kullanıcı adını al
  const getUserName = () => {
    return `${authUser?.name || ''} ${authUser?.surname || ''}`.trim() || 'Kullanıcı';
  };

  if (!authUser) return null;

  return (
    <>
      {/* Floating Action Button */}
      <Zoom in={!open}>
        <Fab
          color="primary"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            width: 60,
            height: 60,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                right: -3,
                top: -3,
                fontSize: 11,
                minWidth: 18,
                height: 18
              }
            }}
          >
            <ChatIcon sx={{ fontSize: 28 }} />
          </Badge>
        </Fab>
      </Zoom>

      {/* Chat Window */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={24}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: minimized ? 300 : 380,
            height: minimized ? 56 : 520,
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: minimized ? 'pointer' : 'default'
            }}
            onClick={minimized ? () => setMinimized(false) : undefined}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              {activeChat ? (
                <>
                  <IconButton 
                    size="small" 
                    onClick={(e) => { e.stopPropagation(); endChat(); }}
                    sx={{ color: 'white', p: 0.5 }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Avatar 
                    src={config.userAuthApiUrl + activeChat.profileImageUrl}
                    sx={{ width: 32, height: 32 }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                      {activeChat.name}
                    </Typography>
                    {typingUser ? (
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        yazıyor...
                      </Typography>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FiberManualRecordIcon sx={{ fontSize: 8, color: '#4ade80' }} />
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          Çevrimiçi
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <SupportAgentIcon />
                  <Box>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                      Canlı Destek
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <FiberManualRecordIcon 
                        sx={{ 
                          fontSize: 8, 
                          color: isConnected ? '#4ade80' : '#fbbf24' 
                        }} 
                      />
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {isConnected ? 'Bağlı' : 'Bağlanıyor...'}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={minimized ? 'Genişlet' : 'Küçült'}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }}
                  sx={{ color: 'white' }}
                >
                  {minimized ? <ExpandLessIcon /> : <MinimizeIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Kapat">
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Content */}
          {!minimized && (
            <Box flex={1} display="flex" flexDirection="column" bgcolor="#f8fafc">
              {!activeChat ? (
                // Destek Listesi ve Mesajlaştığı Kişiler
                <Box flex={1} overflow="auto" p={2}>
                  {loading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
                      <CircularProgress size={40} sx={{ color: '#6366f1' }} />
                      <Typography variant="body2" color="text.secondary" mt={2}>
                        Yükleniyor...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Mesajlaştığı Kişiler */}
                      {conversations.length > 0 && (
                        <>
                          <Typography variant="body2" fontWeight={600} color="text.primary" mb={1.5} px={1}>
                            Mesajlarım ({conversations.length})
                          </Typography>
                          <List disablePadding sx={{ mb: 2 }}>
                            {conversations.map((conv) => (
                              <ListItem
                                key={conv.id}
                                button
                                onClick={() => startChat(conv)}
                                sx={{
                                  borderRadius: 2,
                                  mb: 1,
                                  bgcolor: conv.unreadCount > 0 ? alpha('#6366f1', 0.08) : 'white',
                                  border: conv.unreadCount > 0 ? `1px solid ${alpha('#6366f1', 0.2)}` : '1px solid transparent',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                  '&:hover': { 
                                    bgcolor: alpha('#6366f1', 0.12),
                                    transform: 'translateX(4px)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <ListItemAvatar>
                                  <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                      conv.isOnline ? (
                                        <FiberManualRecordIcon 
                                          sx={{ 
                                            fontSize: 12, 
                                            color: '#4ade80',
                                            bgcolor: 'white',
                                            borderRadius: '50%'
                                          }} 
                                        />
                                      ) : null
                                    }
                                  >
                                    <Avatar src={config.userAuthApiUrl + conv.profileImageUrl}>
                                      <PersonIcon />
                                    </Avatar>
                                  </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" fontWeight={conv.unreadCount > 0 ? 600 : 500}>
                                        {conv.name}
                                      </Typography>
                                      {conv.unreadCount > 0 && (
                                        <Chip 
                                          label={conv.unreadCount} 
                                          size="small" 
                                          color="error"
                                          sx={{ 
                                            height: 18, 
                                            fontSize: '0.7rem',
                                            fontWeight: 600
                                          }} 
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 200
                                      }}
                                    >
                                      {conv.lastMessage || 'Mesaj yok'}
                                    </Typography>
                                  }
                                />
                                <QuestionAnswerIcon sx={{ color: '#6366f1', opacity: 0.6 }} />
                              </ListItem>
                            ))}
                          </List>
                          <Divider sx={{ my: 2 }} />
                        </>
                      )}

                      {/* Aktif Personeller */}
                      {supporters.length > 0 ? (
                        <>
                          <Typography variant="body2" fontWeight={600} color="text.primary" mb={1.5} px={1}>
                            Aktif Personeller ({supporters.length})
                          </Typography>
                          <List disablePadding>
                            {supporters
                              .filter(s => !conversations.some(c => c.id === s.id)) // Zaten conversations'da varsa gösterme
                              .map((supporter) => (
                              <ListItem
                                key={supporter.id}
                                button
                                onClick={() => startChat(supporter)}
                                sx={{
                                  borderRadius: 2,
                                  mb: 1,
                                  bgcolor: 'white',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                  '&:hover': { 
                                    bgcolor: alpha('#6366f1', 0.05),
                                    transform: 'translateX(4px)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <ListItemAvatar>
                                  <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={
                                      <FiberManualRecordIcon 
                                        sx={{ 
                                          fontSize: 12, 
                                          color: '#4ade80',
                                          bgcolor: 'white',
                                          borderRadius: '50%'
                                        }} 
                                      />
                                    }
                                  >
                                    <Avatar src={config.userAuthApiUrl + supporter.profileImageUrl}>
                                      <PersonIcon />
                                    </Avatar>
                                  </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" fontWeight={500}>
                                      {supporter.name}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      Çevrimiçi • Sohbet başlat
                                    </Typography>
                                  }
                                />
                                <QuestionAnswerIcon sx={{ color: '#6366f1', opacity: 0.6 }} />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      ) : conversations.length === 0 && (
                        <Box 
                          display="flex" 
                          flexDirection="column" 
                          alignItems="center" 
                          justifyContent="center" 
                          py={6}
                          px={2}
                          textAlign="center"
                        >
                          <Avatar 
                            sx={{ 
                              width: 64, 
                              height: 64, 
                              bgcolor: alpha('#6366f1', 0.1),
                              color: '#6366f1',
                              mb: 2
                            }}
                          >
                            <SupportAgentIcon sx={{ fontSize: 32 }} />
                          </Avatar>
                          <Typography variant="body1" fontWeight={500} gutterBottom>
                            Şu an aktif personel yok
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Mesajınızı bırakabilirsiniz, en kısa sürede yanıtlanacaktır.
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              ) : (
                <>
                  {/* Messages */}
                  <Box flex={1} overflow="auto" p={2}>
                    {messages.length === 0 ? (
                      <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        height="100%"
                        textAlign="center"
                        px={2}
                      >
                        <QuestionAnswerIcon sx={{ fontSize: 48, color: alpha('#6366f1', 0.3), mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {activeChat.name} ile sohbeti başlatın
                        </Typography>
                      </Box>
                    ) : (
                      messages.map((msg, index) => (
                        <Fade in key={index}>
                          <Box
                            display="flex"
                            justifyContent={msg.type === 'sent' ? 'flex-end' : 'flex-start'}
                            mb={1.5}
                          >
                            {msg.type === 'received' && (
                              <Avatar 
                                src={config.userAuthApiUrl + activeChat.profileImageUrl}
                                sx={{ width: 28, height: 28, mr: 1 }}
                              >
                                <PersonIcon fontSize="small" />
                              </Avatar>
                            )}
                            <Paper
                              elevation={0}
                              sx={{
                                px: 2,
                                py: 1,
                                maxWidth: '75%',
                                borderRadius: msg.type === 'sent' 
                                  ? '16px 16px 4px 16px' 
                                  : '16px 16px 16px 4px',
                                bgcolor: msg.type === 'sent' 
                                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                                  : 'white',
                                background: msg.type === 'sent' 
                                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                                  : 'white',
                                color: msg.type === 'sent' ? 'white' : 'text.primary',
                                boxShadow: msg.type === 'sent' 
                                  ? '0 2px 12px rgba(99, 102, 241, 0.3)'
                                  : '0 1px 3px rgba(0,0,0,0.08)'
                              }}
                            >
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {msg.body}
                              </Typography>
                            </Paper>
                          </Box>
                        </Fade>
                      ))
                    )}
                    
                    {/* Typing indicator */}
                    {typingUser && (
                      <Fade in>
                        <Box display="flex" alignItems="center" gap={1} ml={1}>
                          <Avatar 
                            src={config.userAuthApiUrl + activeChat?.profileImageUrl}
                            sx={{ width: 24, height: 24 }}
                          >
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box 
                            sx={{ 
                              bgcolor: 'white', 
                              px: 1.5, 
                              py: 0.75, 
                              borderRadius: 2,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {typingUser} yazıyor
                              <span className="typing-dots">...</span>
                            </Typography>
                          </Box>
                        </Box>
                      </Fade>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Input */}
                  <Box 
                    p={2} 
                    bgcolor="white" 
                    borderTop="1px solid" 
                    borderColor="divider"
                    display="flex" 
                    gap={1}
                    alignItems="flex-end"
                  >
                    <TextField
                      inputRef={inputRef}
                      fullWidth
                      multiline
                      maxRows={3}
                      size="small"
                      placeholder="Mesajınızı yazın..."
                      value={newMessage}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      disabled={sending}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 3,
                          bgcolor: '#f8fafc',
                          '&:hover': {
                            bgcolor: '#f1f5f9'
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white'
                          }
                        }
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sending}
                      sx={{
                        bgcolor: '#6366f1',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#4f46e5'
                        },
                        '&.Mui-disabled': {
                          bgcolor: alpha('#6366f1', 0.3),
                          color: 'white'
                        }
                      }}
                    >
                      {sending ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                      ) : (
                        <SendIcon />
                      )}
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Paper>
      </Slide>

      {/* CSS for typing animation */}
      <style>
        {`
          @keyframes typing-blink {
            0%, 20% { opacity: 0.2; }
            50% { opacity: 1; }
            80%, 100% { opacity: 0.2; }
          }
          .typing-dots {
            animation: typing-blink 1.4s infinite both;
          }
        `}
      </style>
    </>
  );
};

export default LiveChatWidget;

